import { useState, useCallback } from 'react'
import {
  RoomData,
  Tool,
  ViewMode,
  Wall,
  Opening,
  Soffit,
  Obstacle,
  Point,
} from '../types/room'
import { WallTemplate } from '../components/TemplateDrawer'

export function useRoomBuilder() {
  const [roomData, setRoomData] = useState<RoomData>({
    height: 280,
    walls: [],
    openings: [],
    ceiling: { soffits: [] },
    obstacles: [],
    sunPosition: 45,
  })

  const [activeTool, setActiveTool] = useState<Tool>('select')
  const [viewMode, setViewMode] = useState<ViewMode>('top')
  const [selectedObjectId, setSelectedObjectId] = useState<string | null>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [drawingPoints, setDrawingPoints] = useState<Point[]>([])

  const [isTemplateDrawerOpen, setIsTemplateDrawerOpen] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<WallTemplate>('free')

  const [isPlacingTemplate, setIsPlacingTemplate] = useState(false)
  const [templateStart, setTemplateStart] = useState<Point | null>(null)
  const [templateEnd, setTemplateEnd] = useState<Point | null>(null)

  const [zoom, setZoom] = useState(1)
  const [pan, setPan] = useState<Point>({ x: 0, y: 0 })
  const [sunRadius, setSunRadius] = useState(600)
  const [sunCenter, setSunCenter] = useState<Point>({ x: 1000, y: 1000 })

  // Soffit drawing state
  const [isDrawingSoffit, setIsDrawingSoffit] = useState(false)
  const [soffitPoints, setSoffitPoints] = useState<Point[]>([])
  const [selectedSoffitTemplate, setSelectedSoffitTemplate] =
    useState<WallTemplate>('free')
  const [isPlacingSoffitTemplate, setIsPlacingSoffitTemplate] = useState(false)
  const [soffitTemplateStart, setSoffitTemplateStart] = useState<Point | null>(
    null,
  )

  // Obstacle dragging state
  const [isDraggingObstacle, setIsDraggingObstacle] = useState(false)
  const [draggedObstacleId, setDraggedObstacleId] = useState<string | null>(
    null,
  )

  // Guide lines visibility
  const [showGuideLines, setShowGuideLines] = useState(true)

  // Room rotation and movement state
  const [isRotatingRoom, setIsRotatingRoom] = useState(false)
  const [isMovingRoom, setIsMovingRoom] = useState(false)
  const [roomRotation, setRoomRotation] = useState(0)

  const addWall = useCallback((wall: Wall) => {
    setRoomData((prev) => ({
      ...prev,
      walls: [...prev.walls, wall],
    }))
  }, [])

  const addWalls = useCallback((walls: Wall[]) => {
    setRoomData((prev) => ({
      ...prev,
      walls: [...prev.walls, ...walls],
    }))
  }, [])

  const addOpening = useCallback((opening: Opening) => {
    setRoomData((prev) => ({
      ...prev,
      openings: [...prev.openings, opening],
    }))
  }, [])

  const addSoffit = useCallback((soffit: Soffit) => {
    setRoomData((prev) => ({
      ...prev,
      ceiling: {
        soffits: [...prev.ceiling.soffits, soffit],
      },
    }))
  }, [])

  const addObstacle = useCallback((obstacle: Obstacle) => {
    setRoomData((prev) => ({
      ...prev,
      obstacles: [...prev.obstacles, obstacle],
    }))
  }, [])

  const updateObject = useCallback((id: string, updates: Partial<any>) => {
    setRoomData((prev) => {
      const wallIndex = prev.walls.findIndex((w) => w.id === id)
      if (wallIndex !== -1) {
        const newWalls = [...prev.walls]
        newWalls[wallIndex] = { ...newWalls[wallIndex], ...updates }
        return { ...prev, walls: newWalls }
      }

      const openingIndex = prev.openings.findIndex((o) => o.id === id)
      if (openingIndex !== -1) {
        const newOpenings = [...prev.openings]
        newOpenings[openingIndex] = { ...newOpenings[openingIndex], ...updates }
        return { ...prev, openings: newOpenings }
      }

      const soffitIndex = prev.ceiling.soffits.findIndex((s) => s.id === id)
      if (soffitIndex !== -1) {
        const newSoffits = [...prev.ceiling.soffits]
        newSoffits[soffitIndex] = { ...newSoffits[soffitIndex], ...updates }
        return { ...prev, ceiling: { soffits: newSoffits } }
      }

      const obstacleIndex = prev.obstacles.findIndex((o) => o.id === id)
      if (obstacleIndex !== -1) {
        const newObstacles = [...prev.obstacles]
        newObstacles[obstacleIndex] = {
          ...newObstacles[obstacleIndex],
          ...updates,
        }
        return { ...prev, obstacles: newObstacles }
      }

      return prev
    })
  }, [])

  const deleteObject = useCallback((id: string) => {
    setRoomData((prev) => ({
      ...prev,
      walls: prev.walls.filter((w) => w.id !== id),
      openings: prev.openings.filter((o) => o.id !== id),
      ceiling: {
        soffits: prev.ceiling.soffits.filter((s) => s.id !== id),
      },
      obstacles: prev.obstacles.filter((o) => o.id !== id),
    }))
    setSelectedObjectId(null)
  }, [])

  const setSunPosition = useCallback((degrees: number) => {
    setRoomData((prev) => ({
      ...prev,
      sunPosition: degrees,
    }))
  }, [])

  const zoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev * 1.2, 3))
  }, [])

  const zoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev / 1.2, 0.3))
  }, [])

  const resetView = useCallback(() => {
    setZoom(1)
    setPan({ x: 0, y: 0 })
  }, [])

  const rotateRoom = useCallback((angle: number) => {
    setRoomRotation(angle)
  }, [])

  const moveRoom = useCallback((offset: Point) => {
    setRoomData((prev) => ({
      ...prev,
      walls: prev.walls.map((wall) => ({
        ...wall,
        start: { x: wall.start.x + offset.x, y: wall.start.y + offset.y },
        end: { x: wall.end.x + offset.x, y: wall.end.y + offset.y },
      })),
      obstacles: prev.obstacles.map((obstacle) => ({
        ...obstacle,
        position: {
          x: obstacle.position.x + offset.x,
          y: obstacle.position.y + offset.y,
        },
      })),
      ceiling: {
        soffits: prev.ceiling.soffits.map((soffit) => ({
          ...soffit,
          points: soffit.points.map((p) => ({
            x: p.x + offset.x,
            y: p.y + offset.y,
          })),
        })),
      },
    }))
  }, [])

  const applyRoomRotation = useCallback((angleDelta: number) => {
    setRoomData((prev) => {
      if (prev.walls.length === 0) return prev

      // Calculate room center
      let minX = Infinity,
        maxX = -Infinity
      let minY = Infinity,
        maxY = -Infinity

      prev.walls.forEach((wall) => {
        minX = Math.min(minX, wall.start.x, wall.end.x)
        maxX = Math.max(maxX, wall.start.x, wall.end.x)
        minY = Math.min(minY, wall.start.y, wall.end.y)
        maxY = Math.max(maxY, wall.start.y, wall.end.y)
      })

      const centerX = (minX + maxX) / 2
      const centerY = (minY + maxY) / 2

      const angleRad = (angleDelta * Math.PI) / 180

      // Rotate point around center
      const rotatePoint = (point: Point): Point => {
        const dx = point.x - centerX
        const dy = point.y - centerY
        return {
          x: centerX + dx * Math.cos(angleRad) - dy * Math.sin(angleRad),
          y: centerY + dx * Math.sin(angleRad) + dy * Math.cos(angleRad),
        }
      }

      return {
        ...prev,
        walls: prev.walls.map((wall) => ({
          ...wall,
          start: rotatePoint(wall.start),
          end: rotatePoint(wall.end),
        })),
        obstacles: prev.obstacles.map((obstacle) => ({
          ...obstacle,
          position: rotatePoint(obstacle.position),
        })),
        ceiling: {
          soffits: prev.ceiling.soffits.map((soffit) => ({
            ...soffit,
            points: soffit.points.map(rotatePoint),
          })),
        },
      }
    })
  }, [])

  return {
    roomData,
    activeTool,
    setActiveTool,
    viewMode,
    setViewMode,
    selectedObjectId,
    setSelectedObjectId,
    isDrawing,
    setIsDrawing,
    drawingPoints,
    setDrawingPoints,
    isTemplateDrawerOpen,
    setIsTemplateDrawerOpen,
    selectedTemplate,
    setSelectedTemplate,
    isPlacingTemplate,
    setIsPlacingTemplate,
    templateStart,
    setTemplateStart,
    templateEnd,
    setTemplateEnd,
    zoom,
    setZoom,
    pan,
    setPan,
    sunRadius,
    setSunRadius,
    sunCenter,
    setSunCenter,
    isDrawingSoffit,
    setIsDrawingSoffit,
    soffitPoints,
    setSoffitPoints,
    selectedSoffitTemplate,
    setSelectedSoffitTemplate,
    isPlacingSoffitTemplate,
    setIsPlacingSoffitTemplate,
    soffitTemplateStart,
    setSoffitTemplateStart,
    isDraggingObstacle,
    setIsDraggingObstacle,
    draggedObstacleId,
    setDraggedObstacleId,
    showGuideLines,
    setShowGuideLines,
    isRotatingRoom,
    setIsRotatingRoom,
    isMovingRoom,
    setIsMovingRoom,
    roomRotation,
    addWall,
    addWalls,
    addOpening,
    addSoffit,
    addObstacle,
    updateObject,
    deleteObject,
    setSunPosition,
    zoomIn,
    zoomOut,
    resetView,
    rotateRoom,
    moveRoom,
    applyRoomRotation,
  }
}
